import { Component, type ErrorInfo, type ReactNode } from 'react';

import { t } from '@/shared/i18n';

import styles from './ErrorBoundary.module.scss';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Последний рубеж: ловит ошибки рендера, до которых не добрался ни один экран.
 *
 * Без него любая необработанная ошибка в дереве компонентов оставляет
 * пользователя перед пустым белым экраном без единой подсказки, что делать.
 *
 * Классовый компонент здесь не архаизм: перехват ошибок рендера в React
 * по-прежнему возможен только через componentDidCatch, хука для этого нет.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Пока просто в консоль. Когда появится сбор ошибок, отправка пойдёт
    // отсюда же — это единственное место, куда стекаются падения рендера.
    console.error('Необработанная ошибка интерфейса', error, info.componentStack);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;

    if (!error) {
      return this.props.children;
    }

    return (
      <div className={styles.boundary} role="alert">
        <div className={styles.card}>
          <h1 className={styles.title}>{t.errorBoundary.title}</h1>
          <p className={styles.text}>{t.errorBoundary.text}</p>
          <pre className={styles.details}>{error.message}</pre>
          <button type="button" className={styles.button} onClick={this.handleReload}>
            {t.common.reload}
          </button>
        </div>
      </div>
    );
  }
}
